<template>
  <div class="w-full p-6 md:p-12 bg-white shadow-md flex flex-col gap-5 font-inter unset-bg">
    <h2 class="self-start text-xl md:text-2xl font-semibold text-gray-800">
      {{ $t("login.title") }}
    </h2>
    <!-- <div v-if="loginError" class="mb-4 rounded-md bg-red-50 p-4">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v2m0 4h.01M12 5a7 7 0 11-0.001 14.001A7 7 0 0112 5z" />
        </svg>
        <p class="text-sm text-red-700">Incorrect email or password. Please try again.</p>
      </div>
    </div> -->
    <div v-if="loginError"
      class="flex items-left p-2 mb-1 text-sm text-[#7a2323] border border-red-900 rounded bg-red-50 ">
      <svg class="w-5 h-5 mr-2 text-red-1000" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd"
          d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm0 8a1 1 0 01-2 0v-4a1 1 0 012 0v4z"
          clip-rule="evenodd" />
      </svg>
      <span>{{ loginError }}</span>
    </div>
    <div class="flex flex-col gap-1 w-full">
      <label for="email" class="self-start text-base text-gray-700">{{
        $t("login.email")
      }}</label>
      <input id="email" v-model="email" type="email" autocomplete="username" @keyup.enter="handleLogin" :class="[
        'bg-white border text-gray-700 border-gray-300 rounded-lg px-3 py-4 outline-none focus:border-gray-500 transition w-full border outline-none focus:ring-2',
        submitted && errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200']" />
      <p v-if="submitted && errors.email" id="email-error" class=" text-sm text-red-600 text-left">
       {{t("login.emailRequired") }}
      </p>
    </div>

    <div class="flex flex-col gap-1 w-full">
      <div class="flex items-center justify-between">
        <label for="password" class="text-gray-700">
          {{ $t("login.password") }}</label>
        <BaseButton size="custom" type="button"
          variant="tertiary"
          class="!p-0 gap-1 text-sm !font-medium"
          @click="showPassword = !showPassword" tabindex="-1">
          <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'" class="text-lg"></i>
          <span>{{ $t("login.showPassword") }}</span>
        </BaseButton>
      </div>
      <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password" autocomplete="current-password" @keyup.enter="handleLogin"
        :class="['bg-white border text-gray-700 border-gray-300 rounded-lg px-3 py-4 outline-none focus:border-gray-500 transition w-full',
          submitted && errors.password
            ? 'border-red-500 focus:ring-red-200'
            : 'border-gray-300 focus:ring-gray-200']" />
      <p v-if="submitted && errors.password" class=" text-sm text-red-600 text-left">{{t("login.passwordRequired") }}</p>
    </div>

    <div class="flex flex-col md:flex-row gap-2 justify-between md:items-center text-sm text-gray-700 mt-2 mb-2">
      <label class="flex items-center cursor-pointer text-left">
        <span class="inline-block relative mr-2">
          <input type="checkbox" v-model="rememberMe"
            class="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 checked:bg-[#600e0e]" />
          <span
            class="absolute text-[#fff] opacity-0 peer-checked:opacity-100 top-[3px] left-[3px] pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"
              stroke="currentColor" stroke-width="1">
              <path fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"></path>
            </svg>
          </span>
        </span>
        {{ $t("login.rememberMe") }}
      </label>
      <a href="#" class="text-gray-800 hover:underline font-bold no-underline text-left md:text-right"
        @click.prevent="emit('show-forgot')">{{ $t("login.forgotPassword") }}</a>
    </div>

    <BaseButton size="custom" type="button" class="!bg-[#7a2323] hover:!bg-[#762123] text-btn-on-primary !font-bold h-11 w-full !rounded"
      @click="handleLogin">
      {{ $t("login.button") }}
    </BaseButton>

    <div class="flex justify-center text-sm mt-2 text-gray-700">
      {{ $t("login.noAccountPrompt") }}
      <a href="javascript:void(0)" @click="redirectToSignup"
        class="text-gray-800 hover:underline font-bold ml-2 cursor-pointer no-underline">{{ $t("login.signUpLink")
        }}</a>
    </div>

  
    <!-- Change Password Dialog -->
    <BaseDialog v-model="showChangePassword">
      <div class="text-lg font-semibold mb-2">Change Password</div>
      <div v-if="changePasswordError" class="mb-2 text-red-600">{{ changePasswordError }}</div>
      <PasswordForm @submit="handlePasswordChange" :passwordRules="passwordRules" />
    </BaseDialog>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import authenticationService from "@/services/authenticationService";
import { useI18n } from "vue-i18n";
import commonService from '@/services/commonService';
import { DateTime } from "luxon";
import BaseDialog from "@/components/BaseDialog.vue";
import PasswordForm from "@/components/PasswordForm.vue";
import BaseButton from '@/components/atoms/buttons/BaseButton.vue';

const { locale, t } = useI18n();
const emit = defineEmits(["show-forgot"]);
const email = ref("");
const password = ref("");
const rememberMe = ref(false);
const showPassword = ref(false);
const loginError = ref("");
const changePasswordError = ref(""); 
const showChangePassword = ref(false);

onMounted(() => {
  setCookie("roofhubtz", DateTime.local().offset);
  getUserLocation();
});

const passwordRules = [
  {
    key: "password.length",
    regex: /.{8,}/,
    checklist: () => t("resetPassword.checklist.length"),
  },
  {
    key: "password.uppercase",
    regex: /[A-Z]/,
    checklist: () => t("resetPassword.checklist.uppercase"),
  },
  {
    key: "password.lowercase",
    regex: /[a-z]/,
    checklist: () => t("resetPassword.checklist.lowercase"),
  },
  {
    key: "password.number",
    regex: /[0-9]/,
    checklist: () => t("resetPassword.checklist.number"),
  }
];

function getCookie(cname) {
  let name = cname + "=";
  let ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}
const errors = reactive({
  email: "",
  password: ""
})

const submitted = ref(false)
function validate() {
  errors.email = ""
  errors.password = ""
  if (!email.value) {
    errors.email = t("login.emailRequired");
  }
  if (!password.value) {
    errors.password = t("login.passwordRequired");
  }
  return !errors.email && !errors.password
}
function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

function handlePasswordChange({ newPassword: np, confirmPassword: cp }) {
  authenticationService.changePassword({ password: np, confirmPassword: cp }).then((res) => {
    if (res.success) {
      showChangePassword.value = false;
      setCookie("roofhubauth", '', -365);
      window.location.href = `/`;
    } else {
      changePasswordError.value = res.error;
    }
  });
}

function handleLogin() {
  loginError.value = "";
  submitted.value = true
  if (validate()) {
    authenticationService.validateUser(email.value, password.value).then((res) => {
      let urlParams = new URLSearchParams(window.location.search);
      if (res.success) {
        if (res.passwordExpired) {
          showChangePassword.value = true;
        } else if (res.selectAccount) {
          window.location.href = `/select-account`;
        } else if (urlParams.has("ReturnUrl")) {
          window.location = urlParams.get("ReturnUrl") + window.location.hash;
        } else {
          window.location.href = `/dashboard`;
        }
      } else {
        loginError.value = res.message
        ? t(res.message)
        : t("login.errorInvalidCredentials");
      }
    });

  }
}

function getUserLocation() {
  // OPTIMIZATION 1: Early exit if cookie already exists (saves ~70% of calls)
  const existingLocation = getCookie("roofhublocation");
  if (existingLocation && existingLocation !== "") {
    console.log("Location already cached, skipping geolocation");
    return;
  }

  // OPTIMIZATION 2: Rate limiting - don't retry for 5 minutes after failure (saves ~15% of calls)
  const lastFailureTime = sessionStorage.getItem("geolocate_last_failure");
  const now = Date.now();
  if (lastFailureTime && now - parseInt(lastFailureTime) < 300000) {
    console.log("Geolocation temporarily disabled due to recent failure (5min cooldown)");
    return;
  }

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCookie(
          "roofhublocation",
          position.coords.latitude + "|" + position.coords.longitude,
          365
        );
        // Clear any failure tracking on success
        sessionStorage.removeItem("geolocate_last_failure");
      },
      (error) => {
        // OPTIMIZATION 3: Only call API for real errors, not user denial (saves ~40% of calls)
        console.log("Geolocation error:", error.code, error.message);
        
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          // Network/GPS issues - try API fallback
          console.log("Browser geolocation failed, trying API fallback");
          geolocate();
        } else if (error.code === error.PERMISSION_DENIED) {
          // User denied - don't waste an API call, use default location
          console.log("Geolocation permission denied by user, using default location");
          setCookie("roofhublocation", "33.2000368|-96.7386955", 365);
          sessionStorage.setItem("geolocate_last_failure", Date.now().toString());
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
      }
    );
    
    // Listen for permission change - if user grants permission later, retry
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((permission) => {
        permission.addEventListener('change', () => {
          if (permission.state === 'granted' && !getCookie("roofhublocation")) {
            navigator.geolocation.getCurrentPosition(
              (position) =>
                setCookie(
                  "roofhublocation",
                  position.coords.latitude + "|" + position.coords.longitude,
                  365
                )
            );
          }
        });
      }).catch((err) => {
        // Silently fail if permissions API not supported
        console.log("Permissions API not supported:", err);
      });
    }
  } else {
    // Browser doesn't support geolocation
    console.log("Browser does not support geolocation, trying API fallback");
    geolocate();
  }
}

async function geolocate() {
  // OPTIMIZATION 4: Double-check cookie (another tab may have set it)
  const existingLocation = getCookie("roofhublocation");
  if (existingLocation && existingLocation !== "") {
    console.log("Location cookie found, skipping API call");
    return;
  }

  // OPTIMIZATION 5: Prevent duplicate simultaneous calls (saves ~5% of calls)
  const geolocateInProgress = sessionStorage.getItem("geolocate_in_progress");
  if (geolocateInProgress === "true") {
    console.log("Geolocation API call already in progress, skipping duplicate");
    return;
  }

  try {
    sessionStorage.setItem("geolocate_in_progress", "true");
    console.log("Calling Google Geolocation API...");
    
    const result = await commonService.fetchGeolocation();
    
    if (result.success && result.data?.location) {
      setCookie(
        "roofhublocation",
        result.data.location.lat + "|" + result.data.location.lng,
        365
      );
      console.log("Location set from API:", result.data.location);
      sessionStorage.removeItem("geolocate_last_failure");
    } else {
      // API failed - use fallback default coordinates
      console.warn("Geolocation API failed, using default location");
      setCookie("roofhublocation", "33.2000368|-96.7386955", 365);
      sessionStorage.setItem("geolocate_last_failure", Date.now().toString());
    }
    return result;
  } catch (error) {
    console.error("Geolocation API error:", error);
    setCookie("roofhublocation", "33.2000368|-96.7386955", 365);
    sessionStorage.setItem("geolocate_last_failure", Date.now().toString());
  } finally {
    sessionStorage.removeItem("geolocate_in_progress");
  }
}

function redirectToSignup() {
  window.open(`/register/?language=${locale.value}`, "_self");
}
</script>